import { useId, type ReactNode } from 'react';
import { ArrowDown, ArrowUp, ImagePlus, Trash2 } from 'lucide-react';
import {
  ContentKind,
  countPostChars,
  type SocialPlatform,
  type ThreadConversionChange,
} from '@mg/shared';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Textarea } from '../../components/ui/Field';
import { cn } from '../../lib/cn';
import { MEDIA_KINDS } from './blocks';
import { CharCounter, IconButton } from './EditorControls';
import type { ThreadPostView } from './thread';
import { characterCount } from './ThreadConvertCard';
import { OVER_LIMIT_BORDER, ThreadPostChanges } from './ThreadPostChanges';

/**
 * Registers an element the editor may put focus on, under a name: a text block's key, or a post's
 * key with ':up', ':down' or ':delete'. The editor decides where focus goes after an action. A
 * post's text is its text box, or its read-only "Show changes" view while that is on.
 */
export type FocusTarget = (name: string) => (element: HTMLElement | null) => void;

/** Said in the footer of a post that is over its limit: how far, and that it holds Save. */
export function overLimitMessage(over: number): string {
  return `${characterCount(over)} over the limit. Shorten this post to save.`;
}

/**
 * One post of a thread: a card in the list, with a numbered disc in the rail beside it and a line
 * down to the next post's disc.
 *
 * The card holds the post's text box, its character count, and its media - the same media box as a
 * single post, drawn by the editor for this post alone (`media`). A post that is live, or whose
 * outcome is not known yet, is locked: it says so, and nothing in it can be changed, moved or
 * deleted.
 *
 * Over its limit, a post's count goes red, its text box gets a red border, and its footer says how
 * far over it is and that it holds Save - on every post of any thread, except one that cannot be
 * shortened: a locked post (which does not hold Save either), or a thread that is read-only.
 */
export function ThreadPostCard({
  post,
  number,
  total,
  upDisabled,
  charLimit,
  platform,
  readOnly,
  busy,
  sending,
  focusTarget,
  onFocusText,
  onTextChange,
  onMoveUp,
  onMoveDown,
  onDelete,
  onAddMedia,
  media,
  review,
}: {
  post: ThreadPostView;
  /** 1-based, as people count. */
  number: number;
  total: number;
  /** The first post, or the first one after the locked posts: nothing can move above it. */
  upDisabled: boolean;
  charLimit: number | null;
  /** The destination: its own way of counting is what `charLimit` is measured in. */
  platform: SocialPlatform | null;
  readOnly: boolean;
  busy: boolean;
  sending: boolean;
  focusTarget: FocusTarget;
  onFocusText: (key: string) => void;
  onTextChange: (key: string, text: string) => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onDelete: () => void;
  onAddMedia: () => void;
  /** The post's media box, or nothing when it has no media. */
  media: ReactNode;
  /**
   * "Show changes" after an AI conversion, while it is on: the post's text is shown read-only in
   * place of its text box - with the AI's changes marked when `changes` is given and still spells
   * the text, plain when it is null. Absent: the text box.
   */
  review?: { changes: ThreadConversionChange[] | null };
}) {
  const headingId = useId();
  const counterId = useId();
  const overId = useId();
  const text = post.blocks.find((b) => b.kind === ContentKind.TEXT);
  const locked = post.lock != null;
  const last = number === total;
  // As the platform counts it (on X a link is 23 and an emoji 2), which is what the cap is in.
  const length = text != null ? countPostChars(platform, text.text) : 0;
  const over = charLimit != null && text != null && length > charLimit;
  // Over, and the person's to shorten: a locked post is neither (and the save gates skip it), nor
  // is a post that cannot be edited at all.
  const flagged = over && !locked && !readOnly;
  // The same number the counter shows over the limit.
  const overBy = over ? length - charLimit : 0;
  const describedBy = over ? (flagged ? `${overId} ${counterId}` : counterId) : undefined;
  // The box, once open, is where media is added; a locked post takes none.
  const offersMedia = !readOnly && !locked && !post.blocks.some((b) => MEDIA_KINDS.has(b.kind));
  const counted = charLimit != null && text != null;

  return (
    <li className="flex gap-3">
      <div aria-hidden className="flex flex-col items-center">
        {/* A transparent border: the disc keeps an outline where forced colours drop the fill. */}
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-transparent bg-tint text-sm font-bold text-tint-fg">
          {number}
        </span>
        {/* A border, not a fill, for the same reason. */}
        {!last && <span className="w-0 flex-1 border-l-2 border-line" />}
      </div>
      <div className={cn('min-w-0 flex-1', !last && 'pb-4')}>
        <div
          role="group"
          aria-labelledby={headingId}
          className="rounded-lg border border-line bg-card"
        >
          <div className="flex items-center justify-between gap-2 border-b border-line-soft px-3 py-1.5">
            <div className="flex items-center gap-2">
              <span
                id={headingId}
                className="text-xs font-semibold uppercase tracking-wide text-ink-muted"
              >
                Post {number} of {total}
              </span>
              {post.lock === 'live' && <Badge tone="green">Published</Badge>}
              {post.lock === 'unconfirmed' && <Badge tone="amber">Not confirmed</Badge>}
            </div>
            {!locked && !readOnly && (
              <div className="flex items-center gap-0.5">
                <IconButton
                  ref={focusTarget(`${post.key}:up`)}
                  label={`Move post ${number} up`}
                  disabled={busy || upDisabled}
                  onClick={onMoveUp}
                >
                  <ArrowUp className="h-4 w-4" aria-hidden />
                </IconButton>
                <IconButton
                  ref={focusTarget(`${post.key}:down`)}
                  label={`Move post ${number} down`}
                  disabled={busy || last}
                  onClick={onMoveDown}
                >
                  <ArrowDown className="h-4 w-4" aria-hidden />
                </IconButton>
                <IconButton
                  ref={focusTarget(`${post.key}:delete`)}
                  label={`Delete post ${number}`}
                  disabled={busy}
                  onClick={onDelete}
                  danger
                >
                  <Trash2 className="h-4 w-4" aria-hidden />
                </IconButton>
              </div>
            )}
          </div>
          <div className="space-y-3 p-3">
            {text &&
              (review ? (
                <ThreadPostChanges
                  ref={focusTarget(text.key)}
                  number={number}
                  text={text.text}
                  changes={review.changes}
                  over={flagged}
                  describedBy={describedBy}
                />
              ) : (
                <Textarea
                  ref={focusTarget(text.key)}
                  value={text.text}
                  onChange={(e) => onTextChange(text.key, e.target.value)}
                  onFocus={() => onFocusText(text.key)}
                  rows={4}
                  disabled={readOnly || locked || sending}
                  placeholder="Write here, or generate a draft with AI →"
                  aria-label={`Post ${number} text`}
                  aria-invalid={over || undefined}
                  aria-describedby={describedBy}
                  className={flagged ? OVER_LIMIT_BORDER : undefined}
                />
              ))}
            {(offersMedia || counted) && (
              <div className="flex items-center gap-2">
                {offersMedia && (
                  <Button
                    variant="secondary"
                    size="sm"
                    aria-label={`Add media to post ${number}`}
                    disabled={sending}
                    onClick={onAddMedia}
                  >
                    <ImagePlus className="h-4 w-4" aria-hidden />
                    Add media
                  </Button>
                )}
                {/* Beside the counter, taking the room the counter's margin took; it wraps in its
                    own cell on a narrow card rather than pushing the counter onto a line below. */}
                {flagged && (
                  <p
                    id={overId}
                    className="min-w-0 flex-1 text-right text-xs text-red-600 dark:text-error-dark"
                  >
                    {overLimitMessage(overBy)}
                  </p>
                )}
                {counted && (
                  <CharCounter
                    id={counterId}
                    length={length}
                    limit={charLimit}
                    platform={platform}
                    className={flagged ? undefined : 'ml-auto'}
                  />
                )}
              </div>
            )}
            {media}
          </div>
        </div>
      </div>
    </li>
  );
}
